import { Injectable, inject } from '@angular/core';
import { EnvToken } from '@app/core/env.tokens';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TipsByTags } from '@models/tipsByTags.model';
import { Tip } from '@models/guides/tips.model';
import { urlDecode } from '@utils/urlHelpers';

export interface TipsIndexCategory {
  asCat: number;
  slug: string;
  title?: string;
  categoryLvl?: number;
}

export interface TipsIndexSubCategory extends TipsIndexCategory {
  count: number;
  indexPath: string;
}

export interface TipsIndex {
  category: TipsIndexCategory;
  mainCategory: TipsIndexCategory;
  parentCategory?: TipsIndexCategory;
  indexPath: string;
  tips: Tip[];
  subCategories: TipsIndexSubCategory[];
}

@Injectable({ providedIn: 'root' })
export class TipsService {
  protected readonly env = inject(EnvToken);

  baseHref = this.env.dashboardBaseRef;
  apiPrefix = this.env.apiPrefix;

  constructor(private http: HttpClient) {}

  fetchTipsByTags(): Observable<TipsByTags[]> {
    const url = `${this.baseHref}api/${this.apiPrefix}tips/by-tags`;
    return this.http.get<TipsByTags[]>(url);
  }

  fetchTip(slug: string, mainCat?: string, subCat?: string): Observable<Tip> {
    let params = new HttpParams();
    if (mainCat) {
      const decodedMainCat = urlDecode(mainCat.toLowerCase().trim());
      params = params.append('mainCat', decodedMainCat);
    }
    if (subCat) {
      const decodedSubcat = urlDecode(subCat?.toLowerCase().trim());
      params = params.append('subCat', decodedSubcat);
    }
    const url = `${this.baseHref}api/${this.apiPrefix}tips/detail/${slug}`;
    return this.http.get<Tip>(url, { params });
  }

  fetchRelatedTip(slug: string): Observable<any> {
    const url = `${this.baseHref}api/${this.apiPrefix}tips/related/${slug}`;
    return this.http.get<any>(url);
  }

  fetchTipsByCatSlug(categorySlug: string): Observable<TipsByTags[]> {
    const url = `${this.baseHref}api/${this.apiPrefix}tips/by-cat-slug/${categorySlug}`;
    return this.http.get<TipsByTags[]>(url);
  }

  fetchTipsCatNames(): Observable<TipsByTags[]> {
    const url = `${this.baseHref}api/${this.apiPrefix}tips/cat-names`;
    return this.http.get<TipsByTags[]>(url);
  }

  fetchTipsIndexByCategorySlug(
    categorySlugInput: string,
  ): Observable<TipsIndex> {
    const categorySlug = categorySlugInput?.toLowerCase().trim();
    const url = `${this.baseHref}api/${this.apiPrefix}tips/index-by-category-slug/${categorySlug}`;
    return this.http.get<TipsIndex>(url);
  }

  fetchTipsByAsCat(categorySlug: string): Observable<TipsByTags[]> {
    const url = `${this.baseHref}api/${this.apiPrefix}tips/by-category-slug/${categorySlug}`;
    return this.http.get<TipsByTags[]>(url);
  }
}
